import React, { useState } from "react";
import { Row, Col, Spinner, Form } from "react-bootstrap";
import styled from "styled-components";
import CheckIcon from "@mui/icons-material/Check";
import SuccessButton from "../../../organisms/SuccessButton";
import ServerErrorModal from "../../../organisms/ServerErrorModal";
import { useMutation } from "@tanstack/react-query";
import { buildNuevaCompra, Model } from "../Model";
import CurrencyFormat from "react-currency-format";
import { currencyFormat, parseCurrency } from "../../../../utils/numberUtils";
import ExitoCard, { ExitoOption } from "../../../organisms/ExitoCard";
import { Controller, useForm } from "react-hook-form";
import PaymentIcon from "@mui/icons-material/Payment";
import { MetodoPago } from "../../../../services/ventasServicios";
import { comprar } from "../../../../services/comprasServicios";

const defaultValues: PagarModel = {
  monto: "",
  metodo: "EFECTIVO",
};

function Pagar({ model }: { model: Model }) {
  const [termino, setTermino] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitted },
  } = useForm<PagarModel>({
    defaultValues,
    mode: "all",
  });

  const {
    error: errorMutacionComprar,
    isLoading: mutacionIsLoading,
    mutate,
  } = useMutation(comprar, {
    onSuccess: () => {
      setTermino(true);
    },
  });

  const onSubmit = (pagoModel: PagarModel) => {
    const datosPago = {
      metodo: pagoModel.metodo,
      monto: parseCurrency(pagoModel.monto, "$", ","),
    };

    const newModel: Model = { ...model, datosPago: datosPago };

    const nuevoAgendamiento = buildNuevaCompra(newModel);

    mutate(nuevoAgendamiento);
  };

  const error = errorMutacionComprar;

  if (termino) {
    return <ExitoCard title={"¡Creación exitosa!"} options={exitoOptions} />;
  }

  return (
    <>
      {error && <ServerErrorModal />}
      <Row className="justify-content-center">
        <Col className="col-10">
          <section className="pt-5 mb-4">
            <h3>
              Pago de reserva
              <IconContainer>
                <PaymentIcon sx={{ fontSize: 28 }} />
              </IconContainer>
            </h3>
          </section>
          <section>
            <Form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              validated={isSubmitted}
            >
              <Form.Group className="mb-3" controlId="formMonto">
                <Form.Label>Monto</Form.Label>
                <Controller
                  control={control}
                  name="monto"
                  defaultValue={""}
                  rules={{
                    required: true,
                    minLength: 1,
                    validate: (v) => {
                      const parsedVal = parseCurrency(v, "$", ",");
                      if (parsedVal > 0 && parsedVal <= model.total!!) {
                        return true;
                      }
                      return false;
                    },
                  }}
                  render={({
                    field: { onChange, onBlur, value, ref },
                    fieldState: { invalid, isTouched, isDirty },
                  }) => (
                    <CurrencyFormat
                      customInput={Form.Control}
                      ref={(_ref) => {
                        ref({
                          focus: () => {},
                        });
                      }}
                      value={value}
                      thousandSeparator={","}
                      onBlur={onBlur}
                      isInvalid={isTouched && invalid}
                      required
                      onChange={onChange}
                      prefix={"$"}
                      decimalSeparator={"."}
                      isValid={isDirty && isTouched && !invalid}
                    />
                  )}
                />

                <Form.Control.Feedback type="invalid">
                  Campo debe ser un número mayor a 0 y menor o igual a{" "}
                  {currencyFormat(model.total!!)}.
                </Form.Control.Feedback>
                <Form.Text>
                  {`Total: ${currencyFormat(model.total!!)}`}
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Método de pago</Form.Label>
                <Controller
                  control={control}
                  name="metodo"
                  rules={{ required: true }}
                  render={({ field: { onChange, ref } }) => (
                    <>
                      <Form.Check
                        type="radio"
                        id="metodo_efectivo"
                        label="Efectivo"
                        name="metodo"
                        onChange={(e) => onChange(e.target.value)}
                        value={"EFECTIVO"}
                        required
                        ref={ref}
                      />

                      <Form.Check
                        type="radio"
                        id="metodo_nequi"
                        label="Nequi"
                        name="metodo"
                        onChange={(e) => onChange(e.target.value)}
                        value={"NEQUI"}
                        required
                        ref={ref}
                      />

                      <Form.Check
                        type="radio"
                        id="metodo_daviplata"
                        label="Daviplata"
                        name="metodo"
                        onChange={(e) => onChange(e.target.value)}
                        required
                        value={"DAVIPLATA"}
                        ref={ref}
                      />

                      <Form.Check
                        type="radio"
                        id="metodo_transferencia"
                        label="Transferencia"
                        name="metodo"
                        onChange={(e) => onChange(e.target.value)}
                        required
                        value={"TRANSFERENCIA"}
                        ref={ref}
                      />

                      <Form.Check
                        type="radio"
                        id="metodo_transferencia"
                        label="Tarjeta de Crédito"
                        name="metodo"
                        onChange={(e) => onChange(e.target.value)}
                        required
                        value={"TARJETA_CREDITO"}
                        ref={ref}
                      />
                    </>
                  )}
                />
                {errors.metodo && (
                  <Form.Control.Feedback type="invalid">
                    {errors.metodo.type === "required" && (
                      <span>Requerido</span>
                    )}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Row className="mt-5">
                <div
                  className="d-flex justify-content-end"
                  style={{ gap: "4px" }}
                >
                  <SuccessButton variant="success" type="submit">
                    {!mutacionIsLoading && (
                      <CheckIcon sx={{ fontSize: "18px" }} />
                    )}{" "}
                    {mutacionIsLoading && (
                      <Spinner animation="border" size="sm" />
                    )}
                    Finalizar
                  </SuccessButton>
                </div>
              </Row>
            </Form>
          </section>
        </Col>
      </Row>
    </>
  );
}

const IconContainer = styled("span")`
  display: relative;
  margin-left: 8px;
  color: ${(props) => props.theme.colors.main};
`;

const exitoOptions: ExitoOption[] = [
  {
    description: "Realizar nueva compra",
    path: "/compras/comprar",
    variant: "primary",
  },
];

interface PagarModel {
  metodo: MetodoPago;
  monto: string;
}

export default Pagar;
